/**
 * Сессия пользователя.
 *
 * Здесь только то, что нужно шеллу для решения «пускать или нет».
 * Все остальные данные — через TanStack Query (agents/web-shell.md §5).
 */
import { create } from 'zustand';
import type { AuthUser } from '@alterium/shared';
import { getAccessToken, restoreSession, setAccessToken, setAuthLostHandler } from '../api/client';
import { authApi } from '../api/auth';

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  status: Status;
  user: AuthUser | null;
  setSession: (user: AuthUser) => void;
  clear: () => void;
  bootstrap: () => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'loading',
  user: null,

  setSession: (user) => set({ user, status: 'authenticated' }),

  clear: () => {
    setAccessToken(null);
    set({ user: null, status: 'anonymous' });
  },

  /**
   * Восстановление после перезагрузки: access-токен живёт в памяти и теряется,
   * но refresh-cookie позволяет получить новый.
   */
  bootstrap: async () => {
    if (getAccessToken()) {
      set({ status: 'authenticated' });
      return;
    }

    const token = await restoreSession();
    if (!token) {
      set({ user: null, status: 'anonymous' });
      return;
    }

    try {
      const user = await authApi.me();
      set({ user, status: 'authenticated' });
    } catch {
      get().clear();
    }
  },

  logout: async () => {
    try {
      await authApi.logout();
    } finally {
      get().clear();
    }
  },
}));

// Если обновление сессии не удалось — интерцептор сообщает об этом сюда.
setAuthLostHandler(() => {
  useAuthStore.getState().clear();
});
